import React, { Component } from 'react';
import CircularProgressbar from 'react-circular-progressbar';
import { connect } from 'react-redux';

import { ATTACHMENT_TYPE_LINK } from 'constants/attachments';
import { CALL_OUT_PURPLE, WHITE } from 'constants/colors';
import { ATTACHMENT_OPENED } from 'constants/events';
import { fetchAllAttachmentsCreator } from 'creators/attachments';
import * as analytics from 'utilities/analytics';
import DownloadProgress from 'utilities/download-progress';

import docIcon from 'img/icons/doc.svg';
import imageIcon from 'img/icons/image.svg';
import linkIcon from 'img/icons/link.svg';
import presentationIcon from 'img/icons/presentation.svg';
import spreadsheetIcon from 'img/icons/spreadsheet.svg';
import videoIcon from 'img/icons/video.svg';
import zipIcon from 'img/icons/zip.svg';

import './styles.less';

const CATEGORY_DOCUMENT = 'Document';
const CATEGORY_LINK = 'Link';
const CATEGORY_PRESENTATION = 'Presentation';
const CATEGORY_SPREADSHEET = 'Spreadsheet';
const CATEGORY_IMAGE = 'Image';
const CATEGORY_VIDEO = 'Video';
const CATEGORY_ZIP = 'Zip';

const beginsWithHttpProtocol = /^http(s)?/i;
const endsWithDocExtension = /\.(doc|docx|pages|one)/i;
const endsWithImageExtension = /\.(jpeg|jpg|png|bmp)/i;
const endsWithPresentationExtension = /\.(key|ppt|pptx|pub)/i;
const endsWithSheetExtension = /\.(numbers|xls|xlsx|accdb)/i;
const endsWithArchiveExtension = /\.(zip|gzip|tar|rar|bin)/i;

class AttachmentBarItem extends Component {
  state = {
    downloading: false,
    downloadProgress: 0,
    downloadProgressInstance: null,
    faviconAvailable: true,
  };

  render() {
    const { attachment } = this.props;
    const { downloading, downloadProgress } = this.state;

    const { icon, category } = this.getAttachmentIconAndCategory();

    let actionType;

    if (attachment.type === ATTACHMENT_TYPE_LINK) {
      actionType = 'View';
    } else {
      actionType = 'Download';
    }

    return (
      <div className="attachment-bar-item">
        <div className="info">
          <span>{attachment.name}</span>
          <span onClick={this.handleClick}>
            {actionType} {category}
          </span>
        </div>
        {!downloading && <img onError={this.handleFaviconError} src={icon} />}
        {downloading && (
          <CircularProgressbar
            percentage={downloadProgress}
            strokeWidth={10}
            styles={{
              path: {
                stroke: CALL_OUT_PURPLE,
                strokeLinecap: 'round',
              },
              trail: {
                stroke: WHITE,
              },
            }}
          />
        )}
      </div>
    );
  }

  componentWillUnmount() {
    if (this.state.downloadProgressInstance) {
      this.state.downloadProgressInstance.abort();
    }
  }

  handleFaviconError = () => {
    this.setState({ faviconAvailable: false });
  };

  handleClick = () => {
    const { attachment } = this.props;

    if (attachment.type === ATTACHMENT_TYPE_LINK) {
      analytics.track(ATTACHMENT_OPENED, this.getTrackProperties());

      return window.open(attachment.url, '_blank');
    }

    this.handleDownload();
  };

  handleDownload = () => {
    if (this.state.downloading) {
      return false;
    }

    const {
      attachment: { download_url: downloadUrl, name: downloadName },
    } = this.props;
    const onProgress = percent => this.setState({ downloadProgress: percent });
    const onError = () => this.resetDownloadState();
    const onComplete = () => {
      analytics.track(ATTACHMENT_OPENED, this.getTrackProperties());
      this.resetDownloadState();
      download.save();
    };
    const download = new DownloadProgress(onProgress, onComplete, onError);

    download.fetch(downloadUrl, downloadName);
    this.setState({ downloading: true, downloadProgressInstance: download });
  };

  resetDownloadState = () => {
    this.setState({
      downloading: false,
      downloadProgress: 0,
      downloadProgressInstance: null,
    });
  };

  getTrackProperties = () => {
    const {
      attachment: {
        type: attachment_type,
        url: attachment_url,
        mime,
        name,
        owner_id,
      },
      currentUserIsOwner,
      videoId,
    } = this.props;

    return {
      video_id: videoId,
      is_video_owner: currentUserIsOwner,
      attachment_type,
      attachment_url,
      mime,
      name,
      owner_id,
    };
  };

  getAttachmentIconAndCategory() {
    const {
      attachment: { type: attachmentType },
    } = this.props;

    if (attachmentType === ATTACHMENT_TYPE_LINK) {
      return this.getAttachmentIconAndCategoryForLink();
    }

    return this.getAttachmentIconAndCategoryForUpload();
  }

  getAttachmentIconAndCategoryForLink() {
    let {
      attachment: { url },
    } = this.props;

    url = url.toLowerCase().trim();

    if (!beginsWithHttpProtocol.test(url)) {
      url = `https://${url}`;
    }

    const anchor = document.createElement('a');

    anchor.href = url;

    if (this.state.faviconAvailable) {
      return {
        icon: `https://logo.clearbit.com/${anchor.hostname}`,
        category: CATEGORY_LINK,
      };
    }

    const pathname = anchor.pathname;

    if (endsWithImageExtension.test(pathname)) {
      return { icon: imageIcon, category: CATEGORY_LINK };
    }

    if (endsWithArchiveExtension.test(pathname)) {
      return { icon: zipIcon, category: CATEGORY_LINK };
    }

    if (endsWithPresentationExtension.test(pathname)) {
      return { icon: presentationIcon, category: CATEGORY_LINK };
    }

    if (endsWithDocExtension.test(pathname)) {
      return { icon: docIcon, category: CATEGORY_LINK };
    }

    if (endsWithSheetExtension.test(pathname)) {
      return { icon: spreadsheetIcon, category: CATEGORY_LINK };
    }

    return { icon: linkIcon, category: CATEGORY_LINK };
  }

  getAttachmentIconAndCategoryForUpload() {
    const {
      attachment: { mime },
    } = this.props;

    if (mime.indexOf('image/') === 0) {
      return { icon: imageIcon, category: CATEGORY_IMAGE };
    }

    if (mime.indexOf('video/') === 0) {
      return { icon: videoIcon, category: CATEGORY_VIDEO };
    }

    if (mime.indexOf('epub') > -1) {
      return { icon: presentationIcon, category: CATEGORY_PRESENTATION };
    }

    if (
      mime.indexOf('officedocument') > -1 ||
      mime.indexOf('opendocument') > -1
    ) {
      if (mime.indexOf('.text') > -1 || mime.indexOf('.document') > -1) {
        return { icon: docIcon, category: CATEGORY_DOCUMENT };
      }

      if (mime.indexOf('.presentation') > -1) {
        return { icon: presentationIcon, category: CATEGORY_PRESENTATION };
      }

      if (mime.indexOf('.spreadsheet') > -1 || mime.indexOf('.sheet') > -1) {
        return { icon: spreadsheetIcon, category: CATEGORY_SPREADSHEET };
      }
    }

    if (
      mime.indexOf('zip') > -1 ||
      mime.indexOf('compressed') > -1 ||
      mime.indexOf('application/') === 0
    ) {
      return { icon: zipIcon, category: CATEGORY_ZIP };
    }

    return { icon: docIcon, category: CATEGORY_DOCUMENT };
  }
}

class AttachmentsBar extends Component {
  render() {
    const {
      attachments,
      currentUserIsOwner,
      shouldNotLoad,
      videoId,
    } = this.props;

    if (!attachments.length || shouldNotLoad) {
      return <div className="attachments-bar empty" />;
    }

    return (
      <div className="attachments-bar">
        {attachments.map(attachment => {
          return (
            <AttachmentBarItem
              key={attachment.id}
              attachment={attachment}
              currentUserIsOwner={currentUserIsOwner}
              videoId={videoId}
            />
          );
        })}
      </div>
    );
  }

  componentDidMount() {
    const { attachmentsFetchedOnce, shouldNotLoad } = this.props;

    if (attachmentsFetchedOnce || shouldNotLoad) {
      return;
    }

    this.props.fetchAllAttachmentsCreator();
  }

  componentDidUpdate(prevProps) {
    const { needsPassword, password } = this.props;

    // if we just got the password, fetch comments
    if (needsPassword && prevProps.password == null && password != null) {
      return this.props.fetchAllAttachmentsCreator();
    }
  }
}

const mapStateToProps = state => {
  const {
    attachments,
    fetched_once: attachmentsFetchedOnce,
  } = state.attachments;
  const {
    current_user_is_owner: currentUserIsOwner,
    password,
    video: {
      id: videoId,
      needs_password: needsPassword,
      viewer_needs_permission: viewerNeedsPermission,
    },
  } = state.share_video;

  const shouldNotLoad = (needsPassword && !password) || viewerNeedsPermission;

  return {
    attachments,
    attachmentsFetchedOnce,
    currentUserIsOwner,
    needsPassword,
    password,
    shouldNotLoad,
    videoId,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchAllAttachmentsCreator: () => dispatch(fetchAllAttachmentsCreator()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentsBar);
